import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Plus, Star, Clock, Sparkles, ArrowLeft, Share2, Heart, Loader2, AlertCircle, Eye, Calendar, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWatchlist } from '../hooks/useWatchlist';
import { getVideoById } from '../services/supabaseService';

const MovieDetails = () => {
  const { id } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [actionLoading, setActionLoading] = useState({ like: false });
  const { user } = useAuth();
  const { 
    isInWatchlist, 
    toggleWatchlist, 
    loading: watchlistLoading 
  } = useWatchlist();

  // Charger les détails de la vidéo depuis Supabase
  useEffect(() => {
    const fetchVideoDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await getVideoById(id);
        
        if (response.success && response.data) {
          setContent(response.data);
        } else {
          throw new Error('Vidéo non trouvée');
        }
      } catch (error) {
        console.error('Erreur chargement vidéo:', error);
        setError('Contenu non trouvé');
      } finally {
        setLoading(false);
      }
    };

    fetchVideoDetails();
  }, [id]);
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-pink-400 mx-auto mb-4" />
          <p className="text-gray-400">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Contenu non trouvé</h1>
          <p className="text-gray-400 mb-4">{error || 'Impossible de charger les détails de ce contenu'}</p>
          <Link to="/home" className="text-pink-400 hover:text-pink-300 inline-flex items-center space-x-2">
            <ArrowLeft className="h-4 w-4" />
            <span>Retourner à l'accueil</span>
          </Link>
        </div>
      </div>
    );
  }

  // Helper functions pour afficher les données de la vidéo
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}min ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'processing':
        return { text: 'En traitement', color: 'bg-yellow-600/30 text-yellow-300 border-yellow-500/30' };
      case 'ready':
        return { text: 'Disponible', color: 'bg-green-600/30 text-green-300 border-green-500/30' };
      case 'error':
        return { text: 'Erreur', color: 'bg-red-600/30 text-red-300 border-red-500/30' };
      default:
        return { text: status || 'Inconnu', color: 'bg-gray-600/30 text-gray-300 border-gray-500/30' };
    }
  };

  const statusBadge = getStatusBadge(content.status);
  const isVideoReady = content.status === 'ready' || content.url;

  const toggleWatchlistAction = async () => {
    if (!user) {
      alert('Veuillez vous connecter pour ajouter à votre liste');
      return;
    }
    
    if (!content) return;
    
    await toggleWatchlist(content.id, content.title);
  };

  const toggleLikeAction = async () => {
    if (!user) {
      alert('Veuillez vous connecter pour aimer ce contenu');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, like: true }));
    try {
      // Simulation d'une action async
      await new Promise(resolve => setTimeout(resolve, 800));
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Erreur like:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, like: false }));
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero Section with Backdrop */}
      <section className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={content.thumbnail_url || '/api/placeholder/1920/1080'}
            alt={content.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/api/placeholder/1920/1080';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        </div>

        {/* Back Button */}
        <Link
          to="/home"
          className="absolute top-20 left-4 z-20 flex items-center space-x-2 text-gray-300 hover:text-white transition-colors bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Retour</span>
        </Link>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center lg:text-left">
            {/* AI Creation Badge */}
            <div className="flex items-center justify-center lg:justify-start space-x-2 mb-6">
              <Sparkles className="h-6 w-6 text-pink-400" />
              <span className="text-pink-400 text-sm font-medium uppercase tracking-wider">
                Création IA Exclusive
              </span>
            </div>
            
            {/* Title */}
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-pink-200 to-violet-200 bg-clip-text text-transparent">
              {content.title}
            </h1>

            {/* Status Badge */}
            <div className="flex items-center justify-center lg:justify-start mb-6">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${statusBadge.color}`}>
                {statusBadge.text}
              </span>
            </div>
            
            {/* Meta Information */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start space-x-6 mb-6 text-gray-300">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-blue-400" />
                <span>{formatDate(content.created_at)}</span>
              </div>
              <span>•</span>
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-green-400" />
                <span>{formatDuration(content.duration_seconds)}</span>
              </div>
              <span>•</span>
              <div className="flex items-center space-x-2">
                <Eye className="h-5 w-5 text-purple-400" />
                <span>{formatFileSize(content.file_size)}</span>
              </div>
            </div>

            {/* File Type */}
            <div className="mb-6">
              <span className="inline-block bg-violet-600/30 text-violet-300 px-3 py-1 rounded-full text-sm font-medium border border-violet-500/30">
                {content.file_type}
              </span>
            </div>
            
            {/* Description */}
            <p className="text-lg lg:text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              {content.description}
            </p>
            
            {/* Creator */}
            <div className="flex items-center justify-center lg:justify-start space-x-2 mb-8">
              <User className="h-4 w-4 text-pink-400" />
              <span className="text-pink-400 text-sm">Créé par ID: {content.user_id}</span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              {isVideoReady ? (
                <Link
                  to={content.url || '#'}
                  target={content.url ? "_blank" : "_self"}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-full transition-all duration-300 font-semibold text-lg shadow-lg transform hover:scale-105 ${
                    content.url 
                      ? 'bg-pink-600 hover:bg-pink-700 text-white shadow-pink-600/30 hover:shadow-pink-600/50' 
                      : 'bg-gray-600 cursor-not-allowed text-gray-300 shadow-gray-600/30'
                  }`}
                >
                  <Play className="h-6 w-6 fill-current" />
                  <span>{content.url ? 'Regarder maintenant' : 'En traitement...'}</span>
                </Link>
              ) : (
                <div className="flex items-center space-x-3 bg-gray-600 text-gray-300 px-8 py-4 rounded-full font-semibold text-lg cursor-not-allowed">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span>Traitement en cours...</span>
                </div>
              )}
              
              <button
                onClick={toggleWatchlistAction}
                disabled={!user || watchlistLoading}
                className={`flex items-center space-x-3 px-8 py-4 rounded-full transition-all duration-300 font-semibold text-lg border-2 ${
                  isInWatchlist(content.id)
                    ? 'bg-violet-600 border-violet-600 text-white'
                    : 'bg-transparent border-violet-500 text-violet-400 hover:bg-violet-600/20'
                } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {watchlistLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Plus className={`h-6 w-6 ${isInWatchlist(content.id) ? 'rotate-45' : ''} transition-transform duration-300`} />
                )}
                <span>{isInWatchlist(content.id) ? 'Dans ma liste' : 'Ajouter à ma liste'}</span>
              </button>
            </div>

            {/* Secondary Actions */}
            <div className="flex items-center justify-center lg:justify-start space-x-4">
              <button
                onClick={toggleLikeAction}
                disabled={!user || actionLoading.like}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 border ${
                  isLiked
                    ? 'bg-red-600/20 border-red-500 text-red-400'
                    : 'bg-gray-800/50 border-gray-600 text-gray-400 hover:text-red-400 hover:border-red-500'
                } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {actionLoading.like ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''} transition-all duration-300`} />
                )}
                <span>{isLiked ? 'Aimé' : 'J\'aime'}</span>
              </button>
              
              <button className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gray-800/50 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 transition-all duration-300">
                <Share2 className="h-5 w-5" />
                <span>Partager</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Content Info */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-800">
          <h2 className="text-2xl font-bold text-white mb-6">À propos de cette vidéo</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-pink-400 mb-4">Informations de visionnage</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Disponibilité:</span>
                  <span className={`text-white px-2 py-1 rounded text-sm ${statusBadge.color}`}>
                    {statusBadge.text}
                  </span>
                </div>
                {content.duration_seconds && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Durée:</span>
                    <span className="text-white flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {formatDuration(content.duration_seconds)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Ajouté le:</span>
                  <span className="text-white flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {formatDate(content.created_at)}
                  </span>
                </div>
                {content.views_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Vues:</span>
                    <span className="text-white flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      {content.views_count.toLocaleString()}
                    </span>
                  </div>
                )}
                {content.resolution && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Qualité:</span>
                    <span className="text-white">{content.resolution}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-violet-400 mb-4">Description</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                {content.description || "Aucune description disponible pour ce contenu."}
              </p>
              
              <h3 className="text-lg font-semibold text-blue-400 mb-4">Informations créatives</h3>
              <div className="space-y-3">
                {content.content_type && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type de contenu:</span>
                    <span className="text-white">{content.content_type}</span>
                  </div>
                )}
                {content.release_year && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Année de sortie:</span>
                    <span className="text-white">{content.release_year}</span>
                  </div>
                )}
                {content.created_by && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Créé par:</span>
                    <span className="text-white">{content.created_by}</span>
                  </div>
                )}
                {content.language && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Langue:</span>
                    <span className="text-white">{content.language}</span>
                  </div>
                )}
                {content.ai_model && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Modèle IA utilisé:</span>
                    <span className="text-white flex items-center">
                      <Sparkles className="h-4 w-4 mr-1 text-purple-400" />
                      {content.ai_model}
                    </span>
                  </div>
                )}
                {content.genres && content.genres.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Genres:</span>
                    <div className="flex flex-wrap gap-1">
                      {content.genres.map((genre, index) => (
                        <span key={index} className="bg-gray-700 text-white px-2 py-1 rounded text-xs">
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MovieDetails;